<template>
    <div
        :class="[
            'relative mb-4 border-b border-zinc-200 pb-3 lg:mb-4 lg:border-b-0 lg:pb-3',
            'lg:after:absolute lg:after:inset-x-0 lg:after:bottom-0 lg:after:h-px lg:after:bg-zinc-200',
            'lg:after:shadow-[-50vw_0_0_var(--color-zinc-200),50vw_0_0_var(--color-zinc-200),-100vw_0_0_var(--color-zinc-200),100vw_0_0_var(--color-zinc-200)]',
            'varify-inline-filters:lg:border-b varify-inline-filters:lg:after:hidden',
        ]"
    >
        <!-- On desktop the bottom border runs edge to edge; the side shadows reach past the container without causing scroll. -->
        <div v-if="showTitle" class="mb-1">
            <h1 class="text-2xl font-black wrap-break-word" v-text="title" />
        </div>

        <div
            class="flex flex-wrap items-center justify-between gap-3 lg:flex-nowrap lg:gap-6 varify-inline-filters:lg:flex-wrap varify-inline-filters:lg:gap-3"
        >
            <!-- Header filters sit above the toolbar row on mobile and between the filter button and the result count on desktop. -->
            <div class="contents lg:order-2 lg:block lg:min-w-0 lg:flex-1 varify-inline-filters:lg:order-first varify-inline-filters:lg:basis-full">
                <slot />
            </div>

            <div class="flex min-w-0 items-center gap-3 lg:contents varify-inline-filters:lg:flex">
                <button
                    v-if="filtersEnabled"
                    type="button"
                    class="inline-flex h-10 shrink-0 cursor-pointer items-center gap-4 rounded-md bg-highlight pr-8 pl-4 font-bold text-white lg:order-1 varify-inline-filters:lg:hidden"
                    @click="emit('open-filters')"
                >
                    <SlidersHorizontal class="size-4" :stroke-width="2" />
                    <span>{{ $t('pages.category.filter') }}</span>
                    <span v-if="selectedFilterCount > 0" class="hidden lg:inline" v-text="`(${selectedFilterCount})`" />
                </button>
                <div class="lg:order-3 lg:ml-auto lg:shrink-0 varify-inline-filters:lg:ml-0">
                    <p v-if="resultText" class="text-sm font-medium text-zinc-600" v-text="resultText" />
                    <div v-else-if="!algoliaReady" class="h-5 w-24 animate-pulse rounded bg-zinc-200" />
                    <p v-else class="text-sm font-medium text-zinc-600">
                        {{ nbHits }} {{ resultLabel ?? $t('pages.category.products.label', { count: nbHits }) }}
                    </p>
                </div>
            </div>

            <div class="inline-flex items-center gap-1 lg:hidden varify-inline-filters:lg:inline-flex">
                <button
                    type="button"
                    class="cursor-pointer rounded-sm p-2 transition"
                    :class="!gridView ? 'text-highlight' : 'text-zinc-400 hover:text-zinc-800'"
                    @click="emit('update:gridView', false)"
                >
                    <span class="sr-only varify-inline-filters:lg:hidden" v-text="$t('pages.category.open_list_view')" />
                    <span class="sr-only hidden varify-inline-filters:lg:inline" v-text="$t('pages.category.open_two_column_view')" />
                    <Rows2 class="size-6 varify-inline-filters:lg:hidden" :stroke-width="1.8" />
                    <Grid2x2 class="hidden size-6 varify-inline-filters:lg:block" :stroke-width="1.8" />
                </button>

                <button
                    type="button"
                    class="cursor-pointer rounded-sm p-2 transition"
                    :class="gridView ? 'text-highlight' : 'text-zinc-400 hover:text-zinc-800'"
                    :aria-label="$t('pages.category.open_grid_view')"
                    @click="emit('update:gridView', true)"
                >
                    <Grid2x2 class="size-6 varify-inline-filters:lg:hidden" :stroke-width="1.8" />
                    <Grid3x3 class="hidden size-6 varify-inline-filters:lg:block" :stroke-width="1.8" />
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { Grid2x2, Grid3x3, Rows2, SlidersHorizontal } from '@lucide/vue';

withDefaults(
    defineProps<{
        title: string;
        nbHits: number;
        filtersEnabled: boolean;
        gridView: boolean;
        selectedFilterCount: number;
        algoliaReady: boolean;
        showTitle?: boolean;
        resultLabel?: string;
        resultText: string | undefined;
    }>(),
    {
        showTitle: true,
        resultLabel: undefined,
        resultText: undefined,
    },
);

const emit = defineEmits<{
    'open-filters': [];
    'update:gridView': [value: boolean];
}>();
</script>
